import BottomBar from './BottomBar';

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { FileText } from 'lucide-react';
import {
  calculateOnChange,
  calculateTotals,
  getFormActionButtons,
  getFormTitle,
  recalculateForAllRows,
  transformFormData
} from '../../utils';
import { AritoFormV2, FormHeaderActionsV2, GetDocumentsV2 } from '@/components/custom/erp';
import { createRepeatedTaxRecalculator, useRepeatRowsHandler } from '@/lib/table-handlers';
import { useDetailRows, useFeeDetailRows, useFeeRows, useTaxRows } from './hooks';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formSchema, FormSchema, initialFormValues } from '../../schema';
import { getDocumentsColumns } from './get-documents-columns';
import { useAuth } from '@/contexts/auth-context';
import { useFormFieldState } from '../../hooks';
import InfoDropdown from './InfoDropdown';
import FeeDetailTab from './FeeDetailTab';
import BasicInfoTab from './BasicInfoTab';
import { FormMode } from '@/types/form';
import HistoryTab from './HistoryTab';

import DetailTab from './DetailTab';
import OtherTab from './OtherTab';
import TaxTab from './TaxTab';
import FeeTab from './FeeTab';

interface FormDialogProps {
  formMode: FormMode;
  open?: boolean;
  initialData?: MuaHangRecord;
  onSubmit?: (data: MuaHangRecord) => void;
  onClose: () => void;
  onAdd?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onCopy?: () => void;
}

const recalculateTaxAfterRepeat = createRepeatedTaxRecalculator<MuaHangRecord>({
  taxAmountField: 't_thue_nt',
  getTaxBase: row => Number(row?.t_tien_nt || 0),
  roundValue: value => Math.round(value * 10) / 10
});

const FormDialog = ({
  open,
  formMode,
  initialData,
  onClose,
  onSubmit,
  onAdd,
  onEdit,
  onDelete,
  onCopy
}: FormDialogProps) => {
  const { entityUnit } = useAuth();
  const [openDialog, setOpenDialog] = useState<string>('none');
  const [activeTab, setActiveTab] = useState<string>('info');
  const {
    rows: detailRows,
    selectedRowUuid: detailSelectedRowUuid,
    selectedCell: detailSelectedCell,
    handleRowClick: detailHandleRowClick,
    handleCellClick: detailHandleCellClick,
    setRows: setDetailRows,
    handleDeleteRow: detailHandleDeleteRow,
    handleCopyRow: detailHandleCopyRow,
    handlePasteRow: detailHandlePasteRow,
    handleMoveRow: detailHandleMoveRow,
    handleCellValueChange: detailHandleCellValueChange
  } = useDetailRows(initialData?.chi_tiet_phieu_nhaps || []);
  const {
    rows: taxRows,
    selectedRowUuid: taxSelectedRowUuid,
    selectedCell: taxSelectedCell,
    handleRowClick: taxHandleRowClick,
    handleCellClick: taxHandleCellClick,
    handleAddRow: taxHandleAddRow,
    handleDeleteRow: taxHandleDeleteRow,
    handleCopyRow: taxHandleCopyRow,
    handlePasteRow: taxHandlePasteRow,
    handleMoveRow: taxHandleMoveRow,
    handleCellValueChange: taxHandleCellValueChange,
    setRows: setTaxRows
  } = useTaxRows(initialData?.thue_phieu_nhaps || []);
  const {
    rows: feeRows,
    selectedRowUuid: feeSelectedRowUuid,
    selectedCell: feeSelectedCell,
    handleRowClick: feeHandleRowClick,
    handleCellClick: feeHandleCellClick,
    handleAddRow: feeHandleAddRow,
    handleDeleteRow: feeHandleDeleteRow,
    handleCopyRow: feeHandleCopyRow,
    handlePasteRow: feeHandlePasteRow,
    handleMoveRow: feeHandleMoveRow,
    handleCellValueChange: feeHandleCellValueChange,
    setRows: setFeeRows
  } = useFeeRows(initialData?.chi_phi_phieu_nhaps || []);
  const {
    rows: feeDetailRows,
    selectedRowUuid: feeDetailSelectedRowUuid,
    selectedCell: feeDetailSelectedCell,
    handleRowClick: feeDetailHandleRowClick,
    handleCellClick: feeDetailHandleCellClick,
    handleMoveRow: feeDetailHandleMoveRow,
    handleCellValueChange: feeDetailHandleCellValueChange
  } = useFeeDetailRows(initialData?.chi_phi_chi_tiet_phieu_nhaps || []);
  const { state, actions } = useFormFieldState(initialData);
  const formFieldState = useMemo(() => ({ state, actions }), [actions, state]);

  const handleRepeatDetailRows = useRepeatRowsHandler({
    setRows: setDetailRows,
    markDirty: () => setOpenDialog(prev => (prev === 'dirty' ? prev : 'dirty'))
  });

  const handleRepeatFeeRows = useRepeatRowsHandler({
    setRows: setFeeRows,
    markDirty: () => setOpenDialog(prev => (prev === 'dirty' ? prev : 'dirty'))
  });

  const handleRepeatTaxRows = useRepeatRowsHandler({
    setRows: setTaxRows,
    markDirty: () => setOpenDialog(prev => (prev === 'dirty' ? prev : 'dirty')),
    onAfterRepeat: recalculateTaxAfterRepeat
  });

  const { t_thue, t_tt, t_cp } = useMemo(() => {
    return calculateTotals(detailRows, feeRows, taxRows);
  }, [detailRows, feeRows, taxRows]);

  useEffect(() => {
    if (!open) {
      setOpenDialog('none');
      setActiveTab('info');
    }
  }, [open]);

  useEffect(() => {
    setDetailRows(currentRows => {
      if (currentRows.length === 0) return currentRows;

      return calculateOnChange(currentRows, feeRows, taxRows);
    });
  }, [taxRows, feeRows, setDetailRows]);

  const handleDetailRowCellValueChange = useCallback(
    (rowUuid: string, field: string, newValue: MuaHangRecord) => {
      detailHandleCellValueChange(rowUuid, field, newValue);

      if (field === 'tien0') {
        const updatedRows = detailRows.map(row => (row.uuid === rowUuid ? { ...row, [field]: newValue } : row));

        const rowsWithRecalculated = recalculateForAllRows(updatedRows, feeRows, taxRows);
        setDetailRows(rowsWithRecalculated);
      }
    },
    [detailHandleCellValueChange, detailRows, feeRows, setDetailRows, taxRows]
  );

  const handleFeeRowCellValueChange = useCallback(
    (rowUuid: string, field: string, newValue: MuaHangRecord) => {
      feeHandleCellValueChange(rowUuid, field, newValue);

      if (field === 'tien_cp_nt') {
        const updatedFeeRows = feeRows.map(row => (row.uuid === rowUuid ? { ...row, [field]: newValue } : row));
        const updatedDetailRows = calculateOnChange(detailRows, updatedFeeRows, taxRows);
        setDetailRows(updatedDetailRows);
      }
    },
    [detailRows, feeHandleCellValueChange, feeRows, setDetailRows, taxRows]
  );

  const handleTaxRowCellValueChange = useCallback(
    (rowUuid: string, field: string, newValue: MuaHangRecord) => {
      taxHandleCellValueChange(rowUuid, field, newValue);

      if (field === 't_thue_nt' || field === 'tien_hang_nt') {
        const updatedTaxRows = taxRows.map(row => (row.uuid === rowUuid ? { ...row, [field]: newValue } : row));
        const updatedDetailRows = calculateOnChange(detailRows, feeRows, updatedTaxRows);
        setDetailRows(updatedDetailRows);
      }
    },
    [detailRows, feeRows, setDetailRows, taxHandleCellValueChange, taxRows]
  );

  const handleSubmit = (data: MuaHangRecord) => {
    const formData = transformFormData(data, state, detailRows, taxRows, feeRows, feeDetailRows, entityUnit, {
      t_thue,
      t_tt,
      t_cp
    });
    onSubmit?.(formData);
  };
  const handleSearchData = (searchedData: MuaHangRecord) => {
    const { xprocess, customer, selectedRows } = searchedData;

    if (customer) {
      actions.setNhaCungCap(customer);
    }
    const updatedRows = selectedRows.map((row: MuaHangRecord, index: number) => ({
      ...row,
      line: index + 1,
      tien0: row.tien_nt0,
      thue: 0,
      thue_nt: 0,
      tk_dt_data: row.ma_vt_data?.tk_dt_data,
      tk_gv_data: row.ma_vt_data?.tk_gv_data,
      tk_vt_data: row.ma_vt_data?.tk_khcp_data,
      tk_ck_data: row.ma_vt_data?.tk_ck_data,
      tk_km_data: row.ma_vt_data?.tk_km_data,
      tk_thue_co_data: row.ma_thue_data?.tk_thue_dau_ra_data,
      id_hd: row.hoa_don_data,
      line_hd: {
        uuid: row.uuid,
        line: row.line
      }
    }));

    let mapRows = [];
    if (xprocess === '1') mapRows = [...detailRows, ...updatedRows];
    else mapRows = updatedRows;

    setDetailRows(mapRows);
  };

  const handleClose = () => {
    if (openDialog === 'isDirty' && formMode !== 'view') {
      setOpenDialog('isDirty');
    } else {
      onClose();
    }
  };

  const title = getFormTitle(formMode, activeTab);
  const actionButtons = getFormActionButtons(formMode, activeTab, {
    onAdd,
    onEdit,
    onDelete,
    onCopy,
    handleClose
  });

  if (initialData && formMode === 'add') {
    initialData = {
      ...initialData,
      ngay_ct: new Date().toISOString().split('T')[0],
      ngay_lct: new Date().toISOString().split('T')[0]
    };
  }

  return (
    <>
      <AritoFormV2<FormSchema>
        mode={formMode}
        title={title}
        actionButtons={actionButtons}
        subTitle='Phiếu nhập chi phí mua hàng'
        schema={formSchema}
        initialData={initialData || initialFormValues}
        onSubmit={handleSubmit}
        onClose={onClose}
        headerFields={
          <Tabs value={activeTab} onValueChange={setActiveTab} className='w-full'>
            <div className='flex items-stretch justify-between border-b border-[#d7d7d7] bg-white'>
              <TabsList className='h-8 w-full justify-start rounded-none border-b-0 bg-muted p-0 text-slate-600'>
                <TabsTrigger
                  value='info'
                  className='relative h-8 rounded-none border-b-[3px] border-transparent bg-transparent px-4 py-1 text-[12px] font-semibold text-slate-500 shadow-none ring-0 transition-colors focus-visible:ring-0 data-[state=active]:border-b-main data-[state=active]:bg-main/10 data-[state=active]:text-main data-[state=active]:shadow-none'
                >
                  Thông tin
                </TabsTrigger>
                {formMode === 'view' && (
                  <TabsTrigger
                    value='history'
                    className='relative h-8 rounded-none border-b-[3px] border-transparent bg-transparent px-4 py-1 text-[12px] font-semibold text-slate-500 shadow-none ring-0 transition-colors focus-visible:ring-0 data-[state=active]:border-b-main data-[state=active]:bg-main/10 data-[state=active]:text-main data-[state=active]:shadow-none'
                  >
                    Lịch sử
                  </TabsTrigger>
                )}
              </TabsList>
              <FormHeaderActionsV2
                mode={formMode}
                className='px-0'
                source={{
                  label: 'Hóa đơn mua',
                  onClick: () => setOpenDialog('get-documents')
                }}
                info={<InfoDropdown uuid={initialData?.uuid} ma_ct={initialData?.chung_tu} />}
              />
            </div>
            <TabsContent value='info' className='mt-0 outline-none'>
              <div onChange={() => setOpenDialog('isDirty')}>
                <BasicInfoTab formMode={formMode} formState={{ state, actions }} />
              </div>
            </TabsContent>
            {formMode === 'view' && (
              <TabsContent value='history' className='mt-0 outline-none'>
                <HistoryTab />
              </TabsContent>
            )}
          </Tabs>
        }
        tabs={
          activeTab === 'info' && [
            {
              id: 'details',
              label: 'Chi tiết',
              component: (
                <DetailTab
                  formMode={formMode}
                  rows={detailRows}
                  selectedRowUuid={detailSelectedRowUuid}
                  selectedCell={detailSelectedCell}
                  onRowClick={detailHandleRowClick}
                  onCellClick={detailHandleCellClick}
                  onDeleteRow={detailHandleDeleteRow}
                  onCopyRow={detailHandleCopyRow}
                  onPasteRow={detailHandlePasteRow}
                  onMoveRow={detailHandleMoveRow}
                  onRepeatForRows={handleRepeatDetailRows}
                  onCellValueChange={handleDetailRowCellValueChange}
                />
              )
            },
            {
              id: 'fee',
              label: 'Chi phí',
              component: (
                <FeeTab
                  formMode={formMode}
                  rows={feeRows}
                  selectedRowUuid={feeSelectedRowUuid}
                  selectedCell={feeSelectedCell}
                  onRowClick={feeHandleRowClick}
                  onCellClick={feeHandleCellClick}
                  onAddRow={feeHandleAddRow}
                  onDeleteRow={feeHandleDeleteRow}
                  onCopyRow={feeHandleCopyRow}
                  onPasteRow={feeHandlePasteRow}
                  onMoveRow={feeHandleMoveRow}
                  onRepeatForRows={handleRepeatFeeRows}
                  onCellValueChange={handleFeeRowCellValueChange}
                />
              )
            },
            {
              id: 'fee-detail',
              label: 'Chi phí chi tiết',
              component: (
                <FeeDetailTab
                  formMode={formMode}
                  rows={feeDetailRows}
                  selectedRowUuid={feeDetailSelectedRowUuid}
                  selectedCell={feeDetailSelectedCell}
                  onRowClick={feeDetailHandleRowClick}
                  onCellClick={feeDetailHandleCellClick}
                  onMoveRow={feeDetailHandleMoveRow}
                  onCellValueChange={feeDetailHandleCellValueChange}
                />
              )
            },
            {
              id: 'tax',
              label: 'Thuế',
              component: (
                <TaxTab
                  formMode={formMode}
                  rows={taxRows}
                  selectedRowUuid={taxSelectedRowUuid}
                  selectedCell={taxSelectedCell}
                  state={formFieldState}
                  onRowClick={taxHandleRowClick}
                  onCellClick={taxHandleCellClick}
                  onAddRow={() => taxHandleAddRow(detailRows)}
                  onDeleteRow={taxHandleDeleteRow}
                  onCopyRow={taxHandleCopyRow}
                  onPasteRow={taxHandlePasteRow}
                  onMoveRow={taxHandleMoveRow}
                  onRepeatForRows={handleRepeatTaxRows}
                  onCellValueChange={handleTaxRowCellValueChange}
                />
              )
            },
            {
              id: 'other',
              label: 'Khác',
              component: <OtherTab formMode={formMode} />
            }
          ]
        }
        bottomBar={<BottomBar phi={t_cp} thue={t_thue} tong_thanh_toan={t_tt} />}
      />
      {openDialog === 'get-documents' && (
        <GetDocumentsV2
          onSearch={handleSearchData}
          onClose={() => setOpenDialog('none')}
          chungTuType={'HDMH'}
          detailColumns={getDocumentsColumns()}
        />
      )}
    </>
  );
};

export default FormDialog;
